import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";

import { GlossRoutes } from "../../models/enum/routes.enum";
import { FirstDeviceSetupService } from "../../services/DataService/device-trust/first-device-setup.service";

/**
 * TDE first-device setup screen (GOOGLE_SSO_PLAN.md). Reached instead of `set-password`
 * when a brand-new Google SSO login returns `IsFirstDevice: true` — generates the vault
 * key, self-approves this device, and forces the user to save a recovery key before
 * continuing to the vault. See `FirstDeviceSetupService` for the crypto/API
 * orchestration; this component only drives the UI around it.
 */
@Component({
  selector: "app-first-device-setup",
  templateUrl: "first-device-setup.component.html",
})
export class FirstDeviceSetupComponent implements OnInit {
  loading = true;
  failed = false;
  recoveryKey: string;
  confirmedSaved = false;
  submitting = false;

  constructor(
    private firstDeviceSetupService: FirstDeviceSetupService,
    private router: Router,
    private platformUtilsService: PlatformUtilsService,
    private i18nService: I18nService,
    private logService: LogService,
  ) {}

  async ngOnInit() {
    // Only the isFirstDevice login arms setup. Anything else (direct link, refresh) must not
    // generate keys: it would overwrite an existing account's keypair via POST /accounts/keys.
    if (!this.firstDeviceSetupService.consumeArmedSetup()) {
      this.router.navigate([GlossRoutes.Views]);
      return;
    }
    try {
      this.recoveryKey = await this.firstDeviceSetupService.setUpFirstDevice();
    } catch (e) {
      this.logService.error(e);
      this.failed = true;
      this.platformUtilsService.showToast("error", null, this.i18nService.t("errorOccurred"));
    } finally {
      this.loading = false;
    }
  }

  copyRecoveryKey() {
    this.platformUtilsService.copyToClipboard(this.recoveryKey);
    this.platformUtilsService.showToast(
      "success",
      null,
      this.i18nService.t("valueCopied", this.i18nService.t("recoveryKey")),
    );
  }

  async continue() {
    if (!this.confirmedSaved || this.submitting) {
      return;
    }
    this.submitting = true;
    try {
      await this.firstDeviceSetupService.acknowledgeRecoveryKey();
      // Same post-login destination as the rest of the app (LoginComponent.successRoute).
      this.router.navigate([GlossRoutes.Views]);
    } catch (e) {
      this.logService.error(e);
      this.platformUtilsService.showToast("error", null, this.i18nService.t("errorOccurred"));
    } finally {
      this.submitting = false;
    }
  }
}
